import React, { useState } from 'react';
import { Eye, ChevronDown, ChevronUp } from 'lucide-react';

const IndividualResponses = ({ survey }) => {
  const responses = survey.responses || [];
  const questions = survey.questions || [];
  const [expandedResponse, setExpandedResponse] = useState(null);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getAnswerDisplay = (question, answer) => {
    if (answer === undefined || answer === null || answer === '') return '-';
    const settings = question.settings || {};
    const options = settings.options || question.options || [];

    switch (question.type) {
      case 'multipleChoice':
      case 'dropdown':
        const option = options.find(o => o.id === answer);
        return option?.text || answer;
      case 'checkboxes':
        if (!Array.isArray(answer)) return '-';
        return answer.map(a => {
          const opt = options.find(o => o.id === a);
          return opt?.text || a;
        }).join(', ');
      case 'likertScale':
        const labels = settings.labels || question.labels || [];
        return labels[answer - 1] || answer;
      case 'linearScale':
        return String(answer);
      case 'yesNo':
        return answer === 'yes' ? 'Yes' : 'No';
      default:
        return String(answer);
    }
  };

  const toggleExpand = (responseId) => {
    setExpandedResponse(expandedResponse === responseId ? null : responseId);
  };

  if (responses.length === 0) {
    return (
      <div className="no-responses-msg">
        <p>No responses collected yet.</p>
      </div>
    );
  }

  return (
    <div className="individual-responses">
      <div className="responses-summary">
        <span>{responses.length} response{responses.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="responses-list">
        {responses.map((response, index) => (
          <div key={response.responseId} className="response-card">
            <div 
              className="response-header"
              onClick={() => toggleExpand(response.responseId)}
            >
              <div className="response-info">
                <span className="response-number">Response #{index + 1}</span>
                <span className="response-date">{formatDate(response.submittedAt)}</span>
              </div>
              <button className="expand-btn">
                {expandedResponse === response.responseId ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </button>
            </div>

            {expandedResponse === response.responseId && (
              <div className="response-details">
                {questions.map((question, qIndex) => (
                  <div key={question.id} className="response-answer">
                    <div className="answer-question">
                      <span className="q-number">Q{qIndex + 1}.</span>
                      {question.text}
                    </div>
                    <div className="answer-value">
                      {getAnswerDisplay(question, response.answers?.[question.id])}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <style>{`
        .individual-responses {
          padding: 24px;
        }
        .no-responses-msg {
          text-align: center;
          padding: 48px;
          color: #64748b;
        }
        .responses-summary {
          font-size: 14px;
          color: #64748b;
          margin-bottom: 16px;
        }
        .responses-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .response-card {
          background: #f8fafc;
          border-radius: 12px;
          overflow: hidden;
        }
        .response-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .response-header:hover {
          background: #f1f5f9;
        }
        .response-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .response-number {
          font-weight: 600;
          color: #1e293b;
        }
        .response-date {
          font-size: 13px;
          color: #64748b;
        }
        .expand-btn {
          padding: 4px;
          background: transparent;
          border: none;
          color: #64748b;
          cursor: pointer;
          border-radius: 4px;
        }
        .expand-btn:hover {
          background: #e2e8f0;
        }
        .response-details {
          padding: 0 20px 20px;
          border-top: 1px solid #e2e8f0;
          margin-top: 0;
        }
        .response-answer {
          padding: 16px 0;
          border-bottom: 1px solid #e2e8f0;
        }
        .response-answer:last-child {
          border-bottom: none;
        }
        .answer-question {
          font-size: 13px;
          color: #64748b;
          margin-bottom: 6px;
        }
        .q-number {
          font-weight: 600;
          color: #6366f1;
          margin-right: 6px;
        }
        .answer-value {
          font-size: 15px;
          color: #1e293b;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default IndividualResponses;
